import React, {useEffect, useState} from "react";
import {Button} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationCircle} from "@fortawesome/free-solid-svg-icons/faExclamationCircle";
import ModalControls from "../../common/ModalControls";
import {useNavigate, useLocation} from "react-router-dom";
import {defaultFormState} from "../../../../defaults/Forms";
import {positionDefault} from "../../../../defaults/Items";
import {
  onSaveAndClose,
  compareData,
  restoreFormData,
  onItemsListDeleteButtonClick,
  onCloseDetails
} from "../../../../helpers/ComponentHelper";

import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import SettingsEntityModal from "../../common/SettingsEntityModal";
import TextInput from "../../../elements/TextInput";
import TextArea from "../../../elements/TextArea";
import useCrud from "../../../../hooks/useCrud";
import {Table} from "../../../table/Table";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import Select from "../../../elements/Select";
import NumberInput from "../../../elements/NumberInput";
import Toggler from "../../../elements/Toggler";
import PositionTrainingsCard from "./PositionTrainingsCard";
import PositionCertificationsCard from "./PositionCertificationCard";
import "../../../../css/ResourceForms.css";

const POSITION_LEVELS = [
  {value: "REGULAR", label: "Zwykłe"},
  {value: "FUNCTIONAL", label: "Funkcyjne"},
  {value: "MANAGEMENT", label: "Kierownicze"}
];

const Position = () => {

  const dataUrl = "/positions";
  const departmentsUrl = "/departments";
  const trainingsUrl = "/trainings";
  const certificationsUrl = "/certification-types";
  const zonesUrl = "/zones";
  const locationsUrl = "/locations";
  const relatedItemsUrl = null; // if no need to check it, initialize with null and remove RelatedItemsList from details modal
  const defaultItem = {...positionDefault, positionLevel: "REGULAR"};
  const itemName = "stanowisko";
  const itemNames = "stanowiska";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [trainingsList, setTrainingsList] = useState([]);
  const [certificationsList, setCertificationsList] = useState([]);
  const [zonesList, setZonesList] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [departmentError, setDepartmentError] = useState(false);


  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Skrócona nazwa", accessor: "shortName", sortable: true, searchable: true},
    {label: "Stawka bazowa brutto", accessor: "basicWage", sortable: true, searchable: true},
    {label: "Stawka bazowa netto", accessor: "basicNetWage", sortable: true, searchable: true},
    {label: "Opis", accessor: "description", sortable: true, searchable: true},
    {label: "Dział firmy", accessor: "department", sortable: true, searchable: true, format: (department) => department ? `${department.name}` : "", fieldsToInclude: ['name']},
    {label: "Domyślna lokalizacja", accessor: "defaultLocation", sortable: true, searchable: true, format: (location) => location ? `${location.name}` : "", fieldsToInclude: ['name']},
    {
      label: "Poziom stanowiska",
      accessor: "positionLevel",
      sortable: true,
      searchable: true,
      type: "optionsMap",
      optionsMap: ["Zwykłe->REGULAR", "Funkcyjne->FUNCTIONAL", "Kierownicze->MANAGEMENT"],
      contentMap: "REGULAR='Zwykłe';FUNCTIONAL='Funkcyjne';MANAGEMENT='Kierownicze'",
      colorsMap: "REGULAR=#6c757d;FUNCTIONAL=#7b157e;MANAGEMENT=#5a0f5c"
    },
    {label: "Aktywny", accessor: "active", sortable: true, searchable: false, type: "bool"},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false},
  ];


  const state = {
    itemsList, setItems,
    currentItem, setCurrentItem,
    currentFormState, setCurrentFormState,
    defaultItem, backupItem, setBackupItem, itemChanged, setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  }


  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    }
  }


  const onSaveItemClick = async (e) => {
    e.preventDefault();


    if (!currentItem.name || !currentItem.shortName || !currentItem.basicWage || !currentItem.department) {
      if (!currentItem.name) {
        let nameInput = document.getElementById("name");
        nameInput.classList.add("form-input-invalid");
        nameInput.placeholder = `ten element jest wymagany`;
      }

      if (!currentItem.shortName) {
        let shortNameInput = document.getElementById("shortName");
        shortNameInput.classList.add("form-input-invalid");
        shortNameInput.placeholder = `ten element jest wymagany`;
      }

      if (!currentItem.basicWage) {
        let basicWageInput = document.getElementById("basicWage");
        basicWageInput.classList.add("form-input-invalid");
        basicWageInput.placeholder = `ten element jest wymagany`;
      }

      if (!currentItem.department) {
        setDepartmentError(true);
        console.log("Department missing");
      }

      return;
    }


    let response;
    const item = {
      id: currentItem.id,
      shortName: currentItem.shortName,
      name: currentItem.name,
      basicWage: currentItem.basicWage,
      basicNetWage: currentItem.basicNetWage,
      description: currentItem.description,
      department: currentItem.department && currentItem.department.id > 0 ? {id: currentItem.department.id} : null,
      active: currentItem.active,
      positionLevel: currentItem.positionLevel || "REGULAR",
      rcpZone: currentItem.rcpZone && currentItem.rcpZone.id > 0 ? {id: currentItem.rcpZone.id} : null,
      defaultLocation: currentItem.defaultLocation && currentItem.defaultLocation.id > 0 ? {id: currentItem.defaultLocation.id} : null,
      requiredTrainings: currentItem.requiredTrainings,
      requiredCertifications: currentItem.requiredCertifications
    };


    if (currentFormState.formAddingDataMode) {
      response = await createItem(dataUrl, item, state);
    } else {
      response = await updateItem(`${dataUrl}/${item.id}`, item, state);
    }

    if (response.status === 401 || response.status === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    } else if (response.status === 409) {
      setUpdateConflict(true);

    } else {
      response && onSaveAndClose({state});
    }
  }


  const checkRelatedItems = async (id) => {
    //const data = await getRelatedChildrenByParentId(`${ relatedItemsUrl }/${ id }`, id, setEventsList);
    //data.length === 0 ? setAllowDelete(true) : setAllowDelete(false);
    setAllowDelete(true);
  }


  const onClose = () => {
    setUpdateConflict(false);
    onCloseDetails({state});
  };


  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
    setDepartmentError(false);
  }, [itemChanged])


  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.name);
    }
  }, [allowDelete])


  useEffect(() => {
    const getData = async () => {
      const [itemsData, departmentsData, trainingsData, certificationsData, zonesData, locationsData] =
        await Promise.all([
          getItems(dataUrl),
          getItems(departmentsUrl),
          getItems(trainingsUrl),
          getItems(certificationsUrl),
          getItems(zonesUrl),
          getItems(locationsUrl)
        ]);
      const success =
        itemsData.status === 200 &&
        departmentsData.status === 200 &&
        trainingsData.status === 200 &&
        certificationsData.status === 200 &&
        zonesData.status === 200 &&
        locationsData.status === 200;
      if (success) {
        setLoading(false);
        setItems(itemsData.data);
        setDepartmentsList(departmentsData.data);
        setTrainingsList(trainingsData.data);
        setCertificationsList(certificationsData.data);
        setZonesList(zonesData.data);
        setLocationsList(locationsData.data);

      } else {
        alert("Nie udało się pobrać danych.");
      }
    };
    getData();
  }, [])


  const getCompleteItem = async (id) => {
    const response = await getItems(`${dataUrl}/${id}`);
    if (response.status === 200) {
      return {...response.data, positionLevel: response.data.positionLevel || "REGULAR"};
    } else if (response.status === 401 || response.status === 403) {
      navigate('/login', {state: {from: location}, replace: true})
    } else {
      alert("Nie udało się pobrać danych.");
    }
  }



  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: `Tu możesz dodać nowy element.`,
    formHeader: `Nowy element`,
    buttonTitle: ` Dodaj nowe stanowisko`
  }


  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>
  } else if (itemsList.length > 0) {
    dataSectionContent =
      <Table
        rows={itemsList}
        columns={columns}
        state={state}
        checkRelatedItems={checkRelatedItems}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={relatedItemsUrl}
        getCompleteItem={getCompleteItem}
        tableClassName="resource-list-table resource-crud-table"
        tableContainerClassName="resource-list-table-scroll"
      />
  } else {
    dataSectionContent =
      <div className={"warn_info"}><FontAwesomeIcon
        className={"warning-icon div-visible"} icon={faExclamationCircle}/>&nbsp;&nbsp;&nbsp;NIE ZNALEZIONO ŻADNYCH
        DANYCH, DODAJ NOWY {itemName.toUpperCase()}</div>
  }
  return (
    <div id="layoutSidenav_content" className="settings-entity-page resource-page">
      <AppComponentCardHeader title="Stanowiska" buttonProps={addDataButtonProps}/>

      <div className="container-fluid px-4 py-3">
        <div className="card shadow-sm mb-5 resource-list-card resource-standard-list-card">
          {dataSectionContent}
        </div>
      </div>

      <SettingsEntityModal
        show={currentFormState.showForm}
        onClose={onClose}
        title={currentFormState.formAddingDataMode ? "Nowa pozycja: Stanowisko" : `Edycja: ${currentItem.name || "Stanowisko"}`}
        adding={currentFormState.formAddingDataMode}
        introText="Zdefiniuj nazwę, dział, stawkę bazową oraz wymagane szkolenia i certyfikaty. Te dane są później wykorzystywane przy przypisywaniu pracowników i generowaniu grafików."
        onRestore={() => { setUpdateConflict(false); restoreFormData({state}); }}
        onDelete={onDelete}
        onSubmit={onSaveItemClick}
        state={state}
        size="xl"
      >
        <form id="add-position-form" name="add-position-form" className="settings-form-content-card position-form-card">
          <section className="settings-form-section">
            <div className="settings-form-section-heading">
              <div>
                <span className="settings-form-section-kicker">Podstawowe dane</span>
                <h6>Stanowisko</h6>
              </div>
              <div className="position-active-control">
                <span>Aktywne stanowisko</span>
                <Toggler propertyName="active" state={state}/>
              </div>
            </div>

            <div className="position-form-grid">
              <div className="settings-form-field position-field-name">
                <label htmlFor="name">Nazwa *</label>
                <TextInput propertyName="name" required={true} state={state}/>
                <small>Pełna nazwa widoczna w grafikach, planach i kartotece pracownika.</small>
              </div>

              <div className="settings-form-field position-field-short-name">
                <label htmlFor="shortName">Skrót *</label>
                <TextInput propertyName="shortName" maxlen="10" required={true} state={state}/>
                <small>Krótki kod używany w zwartych widokach.</small>
              </div>

              <div className="settings-form-field">
                <label htmlFor="department">Dział firmy *</label>
                <Select
                  label="Dział firmy"
                  propertyName="departmentId"
                  required={false}
                  state={state}
                  itemsList={departmentsList}
                  itemName="department"
                  displayNames={["name"]}
                />
                {departmentError && (
                  <div className="settings-field-error">
                    <FontAwesomeIcon icon={faInfoCircle}/>
                    <span>Wybierz dział, do którego należy stanowisko.</span>
                  </div>
                )}
              </div>

              <div className="settings-form-field">
                <label htmlFor="positionLevel">Poziom stanowiska</label>
                <select
                  id="positionLevel"
                  className="form-select"
                  value={currentItem.positionLevel || "REGULAR"}
                  onChange={(event) => {
                    const value = event.target.value;
                    setCurrentItem(prev => ({...prev, positionLevel: value}));
                    setItemChanged(prev => !prev);
                    setCurrentFormState(prev => ({...prev, formSaveButtonDisabled: false}));
                  }}
                >
                  {POSITION_LEVELS.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
                <small>Poziom organizacyjny do oznaczania i filtrowania stanowisk. Nie nadaje żadnych uprawnień w aplikacji.</small>
              </div>

              <div className="settings-form-field">
                <label htmlFor="rcpZone">Strefa RCP stanowiska</label>
                <Select
                  label="Strefa RCP"
                  propertyName="rcpZoneId"
                  required={false}
                  state={state}
                  itemsList={zonesList}
                  itemName="rcpZone"
                  displayNames={["name"]}
                />
                <small>Terminal w innej strefie zostanie zapisany jako niezgodny z rejonem stanowiska, dzięki czemu odbicie pozostaje w audycie, ale generuje alert.</small>
              </div>

              <div className="settings-form-field">
                <label htmlFor="defaultLocation">Domyślna lokalizacja</label>
                <Select
                  label="Domyślna lokalizacja"
                  propertyName="defaultLocationId"
                  required={false}
                  state={state}
                  itemsList={locationsList}
                  itemName="defaultLocation"
                  displayNames={["name"]}
                />
                <small>Opcjonalna podpowiedź przy tworzeniu zmiany. Nie wiąże stanowiska na stałe z jednym miejscem i można ją zmienić dla konkretnego odcinka pracy.</small>
              </div>

              <div className="settings-form-field">
                <label htmlFor="basicWage">Stawka bazowa brutto</label>
                <NumberInput propertyName="basicWage" required={false} state={state}/>
                <small>Domyślna stawka brutto wykorzystywana, gdy pracownik nie ma bardziej szczegółowej stawki.</small>
              </div>

              <div className="settings-form-field">
                <label htmlFor="basicNetWage">Stawka bazowa netto</label>
                <NumberInput propertyName="basicNetWage" required={false} state={state}/>
                <small>Opcjonalna stawka netto do raportu pracodawcy. System nie wylicza jej automatycznie z brutto.</small>
              </div>

              <div className="settings-form-field settings-form-field-full">
                <label htmlFor="description">Opis</label>
                <TextArea propertyName="description" required={false} rows="3" state={state}/>
                <small>Opcjonalny opis zakresu stanowiska lub dodatkowych informacji organizacyjnych.</small>
              </div>
            </div>
          </section>

          <section className="settings-form-section settings-form-section-requirements">
            <div className="settings-form-section-heading">
              <div>
                <span className="settings-form-section-kicker">Warunki dopuszczenia</span>
                <h6>Wymagania stanowiska</h6>
              </div>
              <span className="settings-form-section-note">Te wymagania są sprawdzane przy kwalifikowaniu pracownika do grafiku.</span>
            </div>

            <div className="position-requirements-grid">
              <PositionTrainingsCard state={state} trainingsList={trainingsList}/>
              <PositionCertificationsCard state={state} certificationsList={certificationsList}/>
            </div>
          </section>

          {updateConflict && (
            <div className="dictionary-form-error">
              <FontAwesomeIcon icon={faInfoCircle}/>
              <span>Nie można dodać ani zmienić tego elementu, ponieważ istnieje już wpis o tej nazwie i/lub skrócie.</span>
            </div>
          )}
        </form>
      </SettingsEntityModal>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  );
}

export default Position;
